import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, map } from 'rxjs';
import { PromptCardComponent } from '../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { SendTestMessageModalComponent } from '../../../../../components/forms/send-test-message-modal/send-test-message-modal.component';
import { RowPreviewComponent } from '../../partials/row-preview/row-preview.component';
import { RowsToCheckComponent } from '../../partials/rows-to-check/rows-to-check.component';
import { SmartBroadcastService } from '../../../../../services/smart-broadcast/smart-broadcast.service';
import {
  UPLOAD_SOFT_LIMIT,
  countryOptions,
} from '../../../../../services/smart-broadcast/smart-broadcast.utils';
import { formatFileSize } from '../../../../../../utils/utils';
import { describeChar, formatCount } from '../../../../../../utils/sms';

interface ReviewNote {
  tone: 'ok' | 'wait' | 'bad' | 'idle';
  text: string;
  more?: string;
}

/**
 * Step 4 — the whole broadcast in one sentence, what to check, the exact
 * message for any row, a test send, then Send with a confirmation. The
 * server answers 202 straight away and works through the rows itself, so
 * success moves on to the Send step, which follows progress.
 */
@Component({
  selector: 'sb-review',
  standalone: true,
  imports: [PromptCardComponent, SendTestMessageModalComponent, RowPreviewComponent, RowsToCheckComponent],
  templateUrl: './review.component.html',
  styleUrls: ['../../sb-shared.css', './review.component.css'],
})
export class ReviewComponent implements OnInit {
  @Output() back = new EventEmitter<void>();
  /** The server accepted the broadcast. */
  @Output() sent = new EventEmitter<void>();

  store = inject(SmartBroadcastService);
  private destroyRef = inject(DestroyRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @ViewChild('previewPanel') previewPanel?: ElementRef<HTMLElement>;

  confirmOpen = false;
  testOpen = false;
  /** Reading the baseline counts just before the upload. */
  readonly preparing = signal(false);
  /** Messages already carrying this broadcast's name. */
  readonly nameInUse = signal<number | null>(null);

  readonly formatCount = formatCount;

  ngOnInit() {
    this.store.settleNow();
    const subject = this.store.subjectSig().trim();
    if (this.isBrowser && subject) {
      this.store
        .countMessages(subject)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((count) => this.nameInUse.set(count));
    }
  }

  readonly totals = computed(() => this.store.totals());
  readonly stats = computed(() => this.store.numberStats());
  readonly busy = computed(() => this.preparing() || this.store.loading());

  readonly uploadSize = computed(() => formatFileSize(this.store.upload().bytes, 1));

  /** How numbers without a country code are read, after the column name. */
  readonly countryNote = computed(() => {
    const code = this.store.country();
    const name = code ? countryOptions().find((country) => country.code === code)?.name : '';
    return name ? `, local numbers read as ${name}` : ', every number needs its country code';
  });

  /** The previewed row if it sends, otherwise the first row that does. */
  readonly testText = computed(() => {
    const shown = this.store.previewRow();
    const row = this.store.isSending(shown) ? shown : this.store.audience()[0];
    return row === undefined ? '' : this.store.renderRow(row).text;
  });

  readonly notes = computed<ReviewNote[]>(() => {
    const notes: ReviewNote[] = [];
    const totals = this.totals();
    const stats = this.stats();
    const unknown = this.store.usedFields().unknown;

    if (unknown.length) {
      notes.push({
        tone: 'bad',
        text: `${unknown.map((field) => `{{${field}}}`).join(', ')} ${
          unknown.length === 1 ? 'isn’t a column' : 'aren’t columns'
        }, so every message shows ${unknown.length === 1 ? 'it' : 'them'} as written.`,
        more: 'Go back to the message to fix the spelling or insert a field.',
      });
    }
    if (totals.withEmpty) {
      notes.push({
        tone: 'wait',
        text: `${formatCount(totals.withEmpty)} ${
          totals.withEmpty === 1 ? 'message shows a field' : 'messages show a field'
        } as written because the value is missing.`,
        more: 'Add a fallback under Empty values on the Message step, or leave them as they are.',
      });
    }
    const inUse = this.nameInUse();
    if (inUse) {
      notes.push({
        tone: 'wait',
        text: `${formatCount(inUse)} ${inUse === 1 ? 'message already uses' : 'messages already use'} the name “${this.store
          .subjectSig()
          .trim()}”.`,
        more: 'Give this broadcast its own name so its messages and progress stay separate.',
      });
    }
    const bytes = this.store.upload().bytes;
    if (bytes > UPLOAD_SOFT_LIMIT) {
      notes.push({
        tone: 'wait',
        text: `The upload is ${formatFileSize(bytes, 1)}. The server may turn away files over 1 MB.`,
        more: 'If sending fails, split the sheet into smaller files and send each one.',
      });
    }
    if (totals.unicode) {
      const offender = this.firstOffender();
      notes.push({
        tone: 'idle',
        text: `${formatCount(totals.unicode)} ${totals.unicode === 1 ? 'message uses' : 'messages use'} Unicode${
          offender ? ` because of ${describeChar(offender)}` : ''
        }, so each part holds 70 characters instead of 160.`,
      });
    }
    const leftOut: string[] = [];
    if (this.store.dropInvalid() && stats.invalid) leftOut.push(`${formatCount(stats.invalid)} invalid`);
    if (this.store.dedupe() && stats.repeats) leftOut.push(`${formatCount(stats.repeats)} repeated`);
    if (stats.empty) leftOut.push(`${formatCount(stats.empty)} without a number`);
    if (leftOut.length) {
      notes.push({ tone: 'idle', text: `Left out: ${joinWords(leftOut)}.` });
    }
    if (!notes.some((note) => note.tone === 'bad' || note.tone === 'wait')) {
      notes.unshift({ tone: 'ok', text: 'Every message has its values filled in.' });
    }
    return notes;
  });

  private firstOffender(): string | null {
    for (const message of this.store.messages()) {
      if (message?.parts.offender) return message.parts.offender;
    }
    return null;
  }

  get impact(): string {
    const totals = this.totals();
    return `${formatCount(totals.messages)} ${totals.messages === 1 ? 'message' : 'messages'} from ${
      this.store.senderIdSig()
    }, ${formatCount(totals.parts)} SMS ${totals.parts === 1 ? 'part' : 'parts'} in all. They start going out straight away and can’t be recalled.`;
  }

  get canSend(): boolean {
    return this.store.reachable() >= 3 && this.totals().messages > 0 && !this.busy();
  }

  /** Show a row's message in the preview and bring it into view. */
  previewRow(row: number) {
    this.store.previewRow.set(row);
    if (!this.isBrowser) return;
    requestAnimationFrame(() => {
      const panel = this.previewPanel?.nativeElement;
      if (!panel) return;
      const box = panel.getBoundingClientRect();
      if (box.top >= 0 && box.bottom <= window.innerHeight) return;
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  showLongest() {
    const longest = this.totals().longest;
    if (longest >= 0) this.store.previewRow.set(longest);
  }

  askSend() {
    if (this.canSend) this.confirmOpen = true;
  }

  closeConfirm() {
    if (!this.busy()) this.confirmOpen = false;
  }

  send() {
    if (this.busy()) return;
    const subject = this.store.subjectSig().trim();
    this.preparing.set(true);
    // The page treats the whole send as in progress from here (leave dialog,
    // step list, refresh prompt), baseline read included.
    this.store.isLoading = true;
    this.store
      .countBaseline(subject)
      .pipe(
        map((base) => this.withNameCount(base)),
        switchMap((base) => {
          this.preparing.set(false);
          return this.store.send().pipe(map(() => base));
        })
      )
      .subscribe({
        next: (base) => {
          this.confirmOpen = false;
          this.store.markSent(base);
          this.sent.emit();
        },
        error: () => {
          // The reason is shown inline (store.error); the confirmation closes.
          this.preparing.set(false);
          this.store.isLoading = false;
          this.confirmOpen = false;
        },
      });
  }

  /**
   * When the baseline read fails, the count taken as Review opened stands in
   * for it, so a reused name does not show its older messages as progress.
   */
  private withNameCount(base: { total: number | null; notAccepted: number | null }) {
    const known = this.nameInUse();
    if (base.total !== null || known === null) return base;
    return { total: known, notAccepted: known === 0 ? 0 : base.notAccepted };
  }
}

function joinWords(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
